'use client';

import { Popover } from '@base-ui/react/popover';
import { type ReactNode } from 'react';

import { NAVIGATION_URLS } from '@libs/core';
import { Backdrop } from '@libs/shared-ui';

import { trackNavigationHeaderClick } from '../../../../../shared';
import { Trigger } from './components';
import { type AccountPopoverVariant, useAccountPopover } from './use-account-popover';
import './account-popover.scss';

type AccountPopoverProps = {
    accountHref: string;
    children: (helpers: { close: () => void }) => ReactNode;
    variant: AccountPopoverVariant;
};

export function AccountPopover({ accountHref, children, variant }: AccountPopoverProps) {
    const { close, isDesktop, mobileAnchor, portalContainer, rootProps, setPopoverRoot, triggerProps } =
        useAccountPopover({ variant });

    function handleTriggerClick() {
        if (accountHref === NAVIGATION_URLS.velocity.signIn) {
            trackNavigationHeaderClick({
                selection: 'login',
                linkUrl: accountHref,
            });
        }
    }

    return (
        <div ref={setPopoverRoot} className="account-popover">
            <Popover.Root {...rootProps}>
                <Popover.Trigger
                    nativeButton={false}
                    openOnHover={isDesktop}
                    delay={100}
                    closeDelay={100}
                    render={(props, state) => (
                        <Trigger
                            accountHref={accountHref}
                            onTriggerClick={handleTriggerClick}
                            props={props}
                            state={state}
                            {...triggerProps}
                        />
                    )}
                />

                {portalContainer && (
                    <Popover.Portal container={portalContainer}>
                        <Popover.Backdrop render={<Backdrop className="account-popover__backdrop" />} />
                        <Popover.Positioner
                            anchor={isDesktop ? undefined : mobileAnchor}
                            side="bottom"
                            align="center"
                            sideOffset={isDesktop ? 16 : 0}
                            collisionPadding={isDesktop ? 16 : 0}
                            collisionAvoidance={{ fallbackAxisSide: 'none', side: 'none' }}
                            positionMethod="absolute"
                            className="account-popover__positioner"
                        >
                            <Popover.Popup className="account-popover__popup" data-render-mode={variant}>
                                {children({ close })}
                            </Popover.Popup>
                        </Popover.Positioner>
                    </Popover.Portal>
                )}
            </Popover.Root>
        </div>
    );
}
